// Napi nézet oszlopai: a nem átfedő shiftek egy oszlopban, egymás alatt. Minden oszlop
// bal fele a terv (shift és a benne lévő egységek), jobb fele a tény: belépés, tracker,
// takarítások a valós idejükön, a shift kereteitől függetlenül.

const MINUTE = 60000;

const planPlanId = (event) => String(event.id).replace("plan-", "");

function planWindow(event, fallbackMinutes) {
  const start = new Date(event.date).getTime();
  return { start, end: start + (event.estimated_minutes || fallbackMinutes) * MINUTE };
}

// Egy ember egy bejelentkezés: az egymást (2 percen belül) folytató munkamenetek egybeolvadnak.
export function mergeSessions(sessions, keyOf) {
  const sorted = [...sessions].sort((a, b) => new Date(a.start) - new Date(b.start));
  const merged = [];
  sorted.forEach((session) => {
    const last = [...merged].reverse().find((item) => keyOf(item) === keyOf(session));
    const lastEnd = last && (last.end ? new Date(last.end).getTime() : Infinity);
    const sameDay = last && new Date(last.start).toDateString() === new Date(session.start).toDateString();
    if (last && sameDay && new Date(session.start).getTime() - lastEnd <= 2 * MINUTE) {
      if (!session.end || (last.end && new Date(session.end) > new Date(last.end))) last.end = session.end;
    } else if (!last || lastEnd !== Infinity) {
      merged.push({ ...session });
    }
  });
  return merged;
}

function overlaps(a, b) {
  return a.start < b.end && b.start < a.end;
}

// A takarítás a rögzített ideig tart, a loctag-beolvasás a közepére esik.
export function cleaningSpan(cleaning) {
  const read = new Date(cleaning.time).getTime();
  const half = ((cleaning.duration_seconds || 0) * 1000) / 2;
  return { start: read - half, end: read + half, read };
}

// Az egymás utáni, azonos locationhöz tartozó takarítások egy csoportot alkotnak.
export function groupCleaningsByLocation(cleanings) {
  const sorted = [...cleanings].sort((a, b) => new Date(a.time) - new Date(b.time));
  const groups = [];
  sorted.forEach((cleaning) => {
    const span = cleaningSpan(cleaning);
    const last = groups[groups.length - 1];
    if (last && String(last.locationId) === String(cleaning.location_id)) {
      last.items.push({ ...cleaning, ...span });
      last.start = Math.min(last.start, span.start);
      last.end = Math.max(last.end, span.end);
    } else {
      groups.push({
        locationId: cleaning.location_id,
        locationName: cleaning.location_name,
        start: span.start,
        end: span.end,
        items: [{ ...cleaning, ...span }],
      });
    }
  });
  return groups;
}

// A tervezett egység színezése: piros a késés (tervezett kezdéstől a tényleges kezdésig,
// vagy amíg még nem kezdődött el, a jelen pillanatig), türkizzöld a tényleges kezdéstől
// a terv végéig.
export function unitSegments(unit, cleanings, now) {
  const { start, end } = planWindow(unit, 60);
  const first = cleanings.length ? Math.min(...cleanings.map((c) => cleaningSpan(c).start)) : null;
  if (first == null) {
    return now > start ? [{ kind: "late", start, end: Math.min(now, end) }] : [];
  }
  const segments = [];
  if (first > start) segments.push({ kind: "late", start, end: Math.min(first, end) });
  if (first < end) segments.push({ kind: "done", start: Math.max(first, start), end });
  return segments;
}

// Egy egység takarításai: a shift dolgozójától (ha van), a group locationjeiből, a terv
// körüli időben.
function unitCleanings(unit, userId, cleanings, groupsById) {
  const group = groupsById.get(Number(unit.cleaning_group_id));
  if (!group) return [];
  const locations = new Set((group.location_ids || []).map(String));
  const { start, end } = planWindow(unit, 60);
  return cleanings.filter((c) => {
    const t = new Date(c.time).getTime();
    return locations.has(String(c.location_id))
      && (userId == null || String(c.user_id) === String(userId))
      && t >= start - 60 * MINUTE && t <= end + 120 * MINUTE;
  });
}

export function buildDayLanes({ day, plans, actual, groupsById = new Map(), now = Date.now() }) {
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  const dayEnd = dayStart + 24 * 60 * MINUTE;
  const inDay = (event) => {
    const t = new Date(event.date).getTime();
    return event.hasTime && t >= dayStart && t < dayEnd;
  };
  const shifts = plans.filter((p) => p.is_shift && inDay(p))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const shiftIds = new Set(shifts.map(planPlanId));

  // Nem átfedő shiftek egy oszlopba; ugyanannak a dolgozónak a shiftjei lehetőleg együtt.
  const lanes = [];
  shifts.forEach((shift) => {
    const window = planWindow(shift, 480);
    const fits = (lane) => lane.shifts.every((s) => !overlaps(planWindow(s, 480), window));
    const own = lanes.find((lane) => shift.assigned_user_id != null
      && lane.userIds.has(String(shift.assigned_user_id)) && fits(lane));
    const lane = own || lanes.find(fits);
    if (lane) {
      lane.shifts.push(shift);
      if (shift.assigned_user_id != null) lane.userIds.add(String(shift.assigned_user_id));
    } else {
      lanes.push({
        key: `shift-${shift.id}`,
        shifts: [shift],
        userIds: new Set(shift.assigned_user_id != null ? [String(shift.assigned_user_id)] : []),
      });
    }
  });

  const webSessions = mergeSessions(actual?.web_sessions || [], (s) => `u${s.user_id}`);
  const trackerSessions = mergeSessions(actual?.tracker_sessions || [], (s) => `u${s.user_id}-${s.device_id || s.device_name}`);
  const cleanings = actual?.cleanings || [];
  const touchesDay = (start, end) => {
    const s = new Date(start).getTime();
    const e = end ? new Date(end).getTime() : now;
    return s < dayEnd && e >= dayStart;
  };

  // Tényadattal rendelkező, de mára shift nélküli dolgozók saját oszlopot kapnak.
  const userNames = new Map();
  [...webSessions, ...trackerSessions, ...cleanings].forEach((item) => {
    if (item.user_id != null) userNames.set(String(item.user_id), item.user_name);
  });
  const laneOfUser = new Map();
  lanes.forEach((lane) => lane.userIds.forEach((id) => { if (!laneOfUser.has(id)) laneOfUser.set(id, lane); }));
  const activeUsers = new Set([
    ...webSessions.filter((s) => touchesDay(s.start, s.end)).map((s) => String(s.user_id)),
    ...trackerSessions.filter((s) => touchesDay(s.start, s.end)).map((s) => String(s.user_id)),
    ...cleanings.filter((c) => touchesDay(c.time, c.time)).map((c) => String(c.user_id)),
  ]);
  activeUsers.forEach((id) => {
    if (id === "null" || id === "undefined" || laneOfUser.has(id)) return;
    const lane = { key: `user-${id}`, shifts: [], userIds: new Set([id]) };
    lanes.push(lane);
    laneOfUser.set(id, lane);
  });

  const result = lanes.map((lane) => {
    const users = lane.userIds;
    const mine = (item) => item.user_id != null && laneOfUser.get(String(item.user_id)) === lane;
    const facts = [];
    const laneWeb = webSessions.filter((s) => mine(s) && touchesDay(s.start, s.end));
    laneWeb.forEach((s) => {
      facts.push({ kind: "login", time: new Date(s.start).getTime(), label: "Login", user: s.user_name });
      // Ha ugyanabban a percben új belépés követi, az nem kilépés, hanem a régi munkamenet lezárása.
      const end = s.end ? new Date(s.end).getTime() : null;
      const replaced = end != null && laneWeb.some((other) => other !== s && other.user_id === s.user_id
        && Math.abs(new Date(other.start).getTime() - end) <= 2 * MINUTE);
      if (end != null && !replaced) facts.push({ kind: "logout", time: end, label: "Logout", user: s.user_name });
    });
    trackerSessions.filter((s) => mine(s) && touchesDay(s.start, s.end)).forEach((s) => {
      const name = s.device_name || s.device_id;
      facts.push({ kind: "tracker", time: new Date(s.start).getTime(), label: name, user: s.user_name });
      if (s.end) facts.push({ kind: "tracker_off", time: new Date(s.end).getTime(), label: `${name} off`, user: s.user_name });
    });
    const laneCleanings = cleanings.filter((c) => mine(c) && touchesDay(c.time, c.time));
    const units = plans
      .filter((p) => !p.is_shift && p.shift_plan_id != null && inDay(p))
      .map((unit) => {
        const shift = lane.shifts.find((s) => planPlanId(s) === String(unit.shift_plan_id));
        if (!shift) return null;
        const matched = unitCleanings(unit, shift.assigned_user_id, laneCleanings, groupsById);
        return { event: unit, shift, segments: unitSegments(unit, matched, now) };
      })
      .filter(Boolean);
    const names = [...users].map((id) => userNames.get(id)
      || lane.shifts.find((s) => String(s.assigned_user_id) === id)?.assigned_user_name).filter(Boolean);
    return {
      key: lane.key,
      title: names.length ? names.join(" · ") : "Unassigned shift",
      shifts: lane.shifts,
      units,
      facts: facts.sort((a, b) => a.time - b.time),
      cleaningGroups: groupCleaningsByLocation(laneCleanings),
    };
  });

  const others = plans.filter((p) => !p.is_shift && inDay(p)
    && !(p.shift_plan_id != null && shiftIds.has(String(p.shift_plan_id))));
  return { lanes: result, others };
}
